import { useDataGrid, List, ShowButton, EditButton } from '@refinedev/mui'
import { DataGrid, GridColDef } from '@mui/x-data-grid'

import {
  Typography,
  Tooltip,
  IconButton,
  Box,
  Button,
  Link,
} from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import {
  BaseKey,
  useGetIdentity,
  useList,
  useMany,
  useOne,
  useNavigation,
  useGo,
} from '@refinedev/core'

import AssignUserFunderModal from '../../components/AssignUserFunderModal'
import FundersQuickSearch from '../../components/FundersQuickSearch'
import { PaginationControls } from '../../components/PaginationControls'
import { UUID } from 'crypto'
import { User } from '@/utils/auth/auth0'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'

interface FunderRegion {
  funder_uuid: number
  region_id: number
}

interface FunderRegionData {
  data: FunderRegion[][] | []
}

export const FundersList: React.FC = () => {
  const go = useGo()

  const user = useGetIdentity().data as User
  const location = useLocation()

  const [assignUserModalOpen, setAssignUserModalOpen] = useState(false)
  const [selectedFunder, setSelectedFunder] = useState<UUID | null>(null)
  const [rowCurrentAssignee, setRowCurrentAssignee] = useState<string | null>(
    null,
  )

  const { data: userData, isLoading } = useOne({
    resource: 'user-by-email',
    id: user?.email,
    queryOptions: {
      enabled: !!user,
    },
  })

  // Helper: Parse region filter with support for JSON objects and 'no_region'
  const parseRegionFilter = (
    regionIdParam: string | null,
    fieldName: string,
  ) => {
    if (!regionIdParam || regionIdParam === '[object Object]') {
      return []
    }

    if (regionIdParam === 'no_region') {
      return [{ field: fieldName, operator: 'null', value: true }]
    }

    // Try to parse JSON object from UnifiedQuickSearch
    let actualRegionId = regionIdParam
    try {
      const regionObj = JSON.parse(regionIdParam)
      if (regionObj && regionObj.value && regionObj.value.id) {
        actualRegionId = regionObj.value.id
      } else if (regionObj && regionObj.id) {
        actualRegionId = regionObj.id
      }

      if (actualRegionId === 'no_region') {
        return [{ field: fieldName, operator: 'null', value: true }]
      }
    } catch {
      // If parsing fails, use the original value
      actualRegionId = regionIdParam
    }

    return [{ field: fieldName, operator: 'eq', value: actualRegionId }]
  }

  // Helper: Pass date range type directly to backend (backend will handle conversion)
  const parseDateRangeFilter = (dateRangeType: string | null) => {
    if (!dateRangeType) {
      return []
    }
    // Just pass the type to backend - backend will convert to appropriate date filters
    return [{ field: 'date_range_type', operator: 'eq', value: dateRangeType }]
  }

  // Helper: Parse type filter with support for JSON objects from autocomplete
  const parseTypeFilter = (typeParam: string | null) => {
    if (!typeParam) {
      return []
    }

    // Try to parse JSON object from UnifiedQuickSearch autocomplete
    let actualType = typeParam
    try {
      const parsed = JSON.parse(typeParam)
      if (parsed && typeof parsed === 'object' && parsed.value) {
        actualType = parsed.value
      }
    } catch {
      // If parsing fails, use the original value
      actualType = typeParam
    }

    return [{ field: 'type', operator: 'eq', value: actualType }]
  }

  // Build filters from URL parameters for server-side filtering
  const getFiltersFromURL = () => {
    const params = new URLSearchParams(location.search)
    const filters: any[] = []

    // FUNDER-LEVEL FILTERS

    // Handle funder active filter
    if (params.get('active') !== null) {
      filters.push({
        field: 'active',
        operator: 'eq',
        value: params.get('active') === 'true',
      })
    }

    if (params.get('opps_status') !== null) {
      filters.push({
        field: 'opps_status',
        operator: 'eq',
        value: params.get('opps_status'),
      })
    }

    // Handle funder quick search - search name field
    const quickSearch = params.get('q')
    if (quickSearch) {
      filters.push({
        field: 'name',
        operator: 'contains',
        value: quickSearch,
      })
    }

    // Handle funder region filter
    filters.push(...parseRegionFilter(params.get('region_id'), 'region_id'))

    // Handle funder within region filter
    const withinRegion = params.get('within_region')
    if (withinRegion === 'true') {
      filters.push({
        field: 'within_region',
        operator: 'eq',
        value: true,
      })
    }

    // Handle funder date range filters (pass directly to backend)
    filters.push(...parseDateRangeFilter(params.get('date_range_type')))

    // Handle funder type filter
    filters.push(...parseTypeFilter(params.get('type')))

    // Handle exact match filter (for type filtering)
    const exactMatch = params.get('exact_match')
    if (exactMatch === 'true') {
      filters.push({
        field: 'exact_match',
        operator: 'eq',
        value: 'true',
      })
    }

    // OPPORTUNITY-LEVEL FILTERS

    // Handle opportunity name search (opportunity_q)
    const opportunitySearch = params.get('opportunity_q')
    if (opportunitySearch) {
      filters.push({
        field: 'opportunity_q',
        operator: 'contains',
        value: opportunitySearch,
      })
    }

    // Handle opportunity region filter
    filters.push(
      ...parseRegionFilter(
        params.get('opportunity_region_id'),
        'opportunity_region_id',
      ),
    )

    // Handle inherit_regions_from_funder filter
    const inheritFromFunder = params.get('inherit_regions_from_funder')
    if (inheritFromFunder === 'true') {
      filters.push({
        field: 'inherit_regions_from_funder',
        operator: 'eq',
        value: true,
      })
    }

    // Handle opportunity within region filter
    const opportunityWithinRegion = params.get('opportunity_within_region')
    if (opportunityWithinRegion === 'true') {
      filters.push({
        field: 'opportunity_within_region',
        operator: 'eq',
        value: true,
      })
    }

    // Handle profile filter
    const profileFilter = params.get('profile')
    if (profileFilter === 'true') {
      filters.push({
        field: 'profile',
        operator: 'nnull',
        value: true,
      })
    } else if (profileFilter === 'false') {
      filters.push({
        field: 'profile',
        operator: 'null',
        value: true,
      })
    }

    // Handle opportunity date range filters (pass directly to backend)
    const oppDateRangeType = params.get('opportunity_date_range_type')
    if (oppDateRangeType) {
      filters.push({
        field: 'opportunity_date_range_type',
        operator: 'eq',
        value: oppDateRangeType,
      })
    }

    // Handle funding rounds filters (pass directly to backend - backend will handle date calculations)
    const fundingRoundsType = params.get('funding_rounds_type')
    if (fundingRoundsType) {
      // Pass the type to backend - backend will convert to appropriate filters
      filters.push({
        field: 'funding_rounds_type',
        operator: 'eq',
        value: fundingRoundsType,
      })
      // If custom, also pass start and end dates
      if (fundingRoundsType === 'custom') {
        const startDate = params.get('funding_rounds_start_date')
        const endDate = params.get('funding_rounds_end_date')
        if (startDate) {
          filters.push({
            field: 'funding_rounds_start_date',
            operator: 'eq',
            value: startDate,
          })
        }
        if (endDate) {
          filters.push({
            field: 'funding_rounds_end_date',
            operator: 'eq',
            value: endDate,
          })
        }
      }
    }

    // Handle assigned_to filter
    const assignedTo = params.get('assigned_to')
    if (assignedTo === 'unassigned') {
      filters.push({
        field: 'assigned_to',
        operator: 'null',
        value: true,
      })
    } else if (assignedTo && assignedTo !== '[object Object]') {
      filters.push({
        field: 'assigned_to',
        operator: 'eq',
        value: assignedTo,
      })
    }

    return filters
  }

  const { dataGridProps, current, setCurrent, pageSize, setPageSize } =
    useDataGrid({
      resource: 'funders',
      filters: {
        permanent: getFiltersFromURL(),
      },
      sorters: {
        initial: [
          {
            field: 'next_check_in_date',
            order: 'desc',
          },
        ],
      },
      errorNotification: (error) => {
        return {
          message: 'Error getting funders',
          description:
            error?.message || 'An error occurred while fetching funders',
          type: 'error',
        }
      },
    })

  const { data: funderAssignee, isLoading: isLoadingFunderAssignee } = useList({
    resource: 'assigned-funders',

    queryOptions: {
      enabled: !!user,
    },
    errorNotification: (error) => {
      return {
        message: 'Error getting funders assignees',
        description:
          error?.message ||
          'An error occurred while fetching funders assignees',
        type: 'error',
      }
    },
  })

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 400,
      filterable: true,
    },
    {
      field: 'type',
      headerName: 'Funder Type',
      flex: 1,
      minWidth: 75,
      filterable: true,
      // type: 'singleSelect',
      // valueOptions: [
      //   'Government',
      //   'Corporate',
      //   'Foundation',
      //   'Iwi/Māori',
      //   'Community',
      //   'Individual',
      // ],
    },
    {
      field: 'region_names',
      headerName: 'Locations',
      flex: 1,
      minWidth: 100,
      filterable: true,
      valueGetter: (params) => params.row.region_names?.join(', ') || '',
      renderCell: function render({ row }) {
        const regionNames = row.region_names?.join(', ') ?? ''
        return (
          <Typography align="center" variant="body2" sx={{ overflow: 'auto' }}>
            {regionNames}
          </Typography>
        )
      },
    },
    {
      field: 'opps_status',
      headerName: 'Opps Status',
      flex: 0.35,
      minWidth: 100,
      align: 'center',
      headerAlign: 'center',
      filterable: false,
      sortable: false,
      renderCell: function render({ row }) {
        if (!row.opps_status) return null
        const color =
          row.opps_status === 'green'
            ? '#4caf50'
            : row.opps_status === 'yellow'
              ? '#ff9800'
              : row.opps_status === 'red'
                ? '#f44336'
                : '#9e9e9e'
        return (
          <FiberManualRecordIcon sx={{ fontSize: '1.2rem', color }} />
        )
      },
    },
    {
      field: 'active',
      headerName: 'Active',
      flex: 0.25,
      minWidth: 75,
      align: 'center',
      headerAlign: 'center',
      filterable: true,
      type: 'boolean',
      valueGetter: (params) => params.row.active,
      renderCell: function render({ row }) {
        return (
          <FiberManualRecordIcon
            sx={{
              fontSize: '1.2rem',
              color: row.active ? '#4caf50' : '#f44336',
            }}
          />
        )
      },
    },
    {
      field: 'next_check_in_date',
      headerName: 'Next Check-in',
      sortable: true,
      filterable: true,
      type: 'date',
      valueGetter: (params) => {
        return params.row.next_check_in_date
          ? new Date(params.row.next_check_in_date)
          : null
      },
      renderCell: function render({ row }) {
        return (
          <Typography>
            {row.next_check_in_date
              ? new Date(row.next_check_in_date).toLocaleDateString('en-NZ', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
              : ''}
          </Typography>
        )
      },
      minWidth: 120,
      align: 'center',
      flex: 1,
    },
    {
      field: 'assigned_to',
      headerName: 'Assigned To',
      flex: 1,
      sortable: false,
      minWidth: 80,
      maxWidth: 200,
      filterable: true,
      valueGetter: (params) => {
        const assignedFunder = funderAssignee?.data.find(
          (fa) => fa.funder_uuid === params.row.uuid,
        )
        return assignedFunder ? assignedFunder.assigned_to_user : 'Unassigned'
      },
      renderCell: function render({ row }) {
        return (
          <Box
            key={row.uuid}
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {funderAssignee &&
              (() => {
                const assignedFunder = funderAssignee.data.find(
                  (funderAssignee) => funderAssignee.funder_uuid === row.uuid,
                )
                return assignedFunder ? (
                  <>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleAddEditAssignee(
                          row.uuid,
                          assignedFunder.assigned_to_user_uuid,
                        )
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <Typography variant="body2">
                      {assignedFunder.assigned_to_user}
                    </Typography>
                  </>
                ) : (
                  <>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleAddEditAssignee(row.uuid, null)
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                    <Typography variant="body2">Unassigned</Typography>
                  </>
                )
              })()}
          </Box>
        )
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      renderCell: function render({ row }) {
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <EditButton hideText recordItemId={row.uuid} />
            <ShowButton hideText recordItemId={row.uuid} />
          </div>
        )
      },
      align: 'center',
      headerAlign: 'center',
      minWidth: 80,
    },
  ]

  function handleAddEditAssignee(row_uuid: UUID, assigned_to_user_uuid: null) {
    setSelectedFunder(row_uuid)
    setAssignUserModalOpen(true)
    setRowCurrentAssignee(assigned_to_user_uuid)
  }

  // const handleRowClick = (params: any) => {
  //   show('funders', params.row.uuid)
  // }

  return (
    <>
      <Typography variant="h5" sx={{ mt: 1, mb: 3 }}>
        {user?.email && userData ? (
          <Button variant="contained" sx={{ mb: 2, color: 'white' }}>
            <Link
              href={`/funders/assigned/${userData.data}`}
              style={{ color: 'white' }}
            >
              Show what I'm assigned to
            </Link>
          </Button>
        ) : (
          'Loading...'
        )}
      </Typography>
      {assignUserModalOpen && (
        <AssignUserFunderModal
          open={assignUserModalOpen}
          onClose={() => setAssignUserModalOpen(false)}
          funder_uuid={selectedFunder}
          currentUser={user}
          currentAssignedUser={rowCurrentAssignee}
        />
      )}
      <List>
        <FundersQuickSearch />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <PaginationControls
            currentPage={current}
            pageSize={pageSize}
            totalCount={dataGridProps.rowCount || 0}
            pageSizeOptions={[10, 25, 50, 100]}
            onPageChange={setCurrent}
            onPageSizeChange={setPageSize}
          />
        </Box>
        <DataGrid
          {...dataGridProps}
          columns={columns}
          autoHeight
          pageSizeOptions={[10, 25, 50, 100]}
          getRowId={(row) => row.uuid}
          disableColumnFilter={false}
          onRowClick={(params, event) => {
            // Check if cmd/ctrl key is pressed for opening in new tab
            if (event.metaKey || event.ctrlKey) {
              // Open in new tab
              window.open(`/funders/${params.row.uuid}`, '_blank')
            } else {
              // Normal navigation
              go({
                to: {
                  resource: 'funders',
                  action: 'show',
                  id: params.row.uuid,
                },
              })
            }
          }}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
        />
      </List>
    </>
  )
}
